import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { AsYouType, CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js'
import { usePathname, useSearchParams } from 'next/navigation'
import React, { useEffect, useMemo, useState } from 'react'

import { convertNumberToPercentage } from '@/utilities/numbers'

import InfoIcon from '../../../../public/svg/outline-info.svg'
import { PhnxButton } from '../../01-atoms/Button/Button'
import { PhnxForminput } from '../../01-atoms/Forminput/Forminput'
import { PhnxFormselect } from '../../01-atoms/Formselect/Formselect'
import { PhnxFormSlider } from '../../01-atoms/FormSlider/FormSlider'
import { CalcFormField, Rating } from './Blok'
import styles from './CalculatorForm.module.css'
import Results from './components/Results/Results'
import industryDataImport from './data/industry-data.json'
import { IndustryData, IndustryDataEntry, ProcessedFormFields } from './types'

const industryData = industryDataImport as unknown as IndustryData

interface Disclaimer {
  type?: string
  content?: string
  richtext?: string
  [key: string]: unknown
}

interface CalculatorFormProps {
  formFields: CalcFormField[]
  ratings: Rating[]
  disclaimer: Disclaimer | React.ReactNode
  initialFormData?: Record<string, string>
  showResults?: boolean
  hideResultsScreen?: boolean
  onFormSubmit?: (formData: Record<string, string>) => void
  printData?: React.ReactNode
  onDownload?: () => Promise<void>
}

interface SelectOption {
  value: string
  label: string
}

const CalculatorForm: React.FC<CalculatorFormProps> = ({
  formFields = [],
  ratings,
  disclaimer,
  initialFormData,
  showResults = false,
  hideResultsScreen = false,
  onFormSubmit,
  printData,
  onDownload,
}) => {
  if (formFields === null || formFields === undefined || formFields.length === 0) {
    console.warn('CalculatorForm: formFields prop is required but was not provided or is empty')
    return null
  }

  const [currentStep, setCurrentStep] = useState(1)
  const [calculatedScores, setCalculatedScores] = useState({
    profitability: '',
    capitalManagement: '',
    growthReadiness: '',
  })
  const [scoresReady, setScoresReady] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isClientSide, setIsClientSide] = useState(false)
  const [highestStepReached, setHighestStepReached] = useState(1)
  const [phoneError, setPhoneError] = useState<boolean>(false)
  const searchParams = useSearchParams()
  const pathname = usePathname()

  // Check if we're on client side
  useEffect(() => {
    setIsClientSide(true)
  }, [])

  // Helper function to map dropdown values to their labels based on form fields
  const mapDropdownValueToLabel = (fieldKey: string, value: string): string => {
    // Find the field definition to get its options
    const field = formFields.find((f) => f.key === fieldKey && f.type === 'dropdown')
    if (!field || !field.options) {
      return value // Return original value if no mapping found
    }

    // Parse the options string to find the matching label
    const options = field.options.split('\n')
    for (const option of options) {
      const [optionValue, ...labelParts] = option.split(':')
      if (optionValue.trim() === value) {
        const fullLabel = labelParts.join(':').trim()
        // Extract just the first part (High, Medium, Low) before the colon
        const labelMatch = fullLabel.match(/^(High|Medium|Low)/)
        return labelMatch ? labelMatch[1] : fullLabel
      }
    }

    return value // Return original value if no match found
  }

  // Group fields by steps
  const steps = useMemo(() => {
    let currentStepNumber = 1
    return formFields.reduce((acc: { [key: number]: CalcFormField[] }, field) => {
      if (field.component === 'calcPageBreak') {
        currentStepNumber = parseInt(field.title.replace('Step ', ''))
        acc[currentStepNumber as number] = []
      } else {
        if (acc[currentStepNumber as number] === undefined) {
          acc[currentStepNumber as number] = []
        }
        acc[currentStepNumber as number]?.push(field)
      }
      return acc
    }, {})
  }, [formFields])

  // Helper function to generate consistent field names
  const getFieldName = (field: CalcFormField) => {
    // if key is undefined, return an empty string
    if (!field.key) {
      return ''
    }
    return field.key.toLowerCase().replace(/\s+/g, '_')
  }
  const totalSteps = useMemo(() => Object.keys(steps).length, [steps])

  // Transform formFields from Storyblok format to the expected format
  const processedFormFields = useMemo(() => {
    return formFields.reduce((acc: ProcessedFormFields, field) => {
      if (field.component === 'calcFormHeading') {
        acc[`h_${field.title.toLowerCase().replace(/\s+/g, '')}`] = {
          type: 'heading',
          title: field.title,
          width: parseInt(field.width) || 12,
        }
      } else if (field.component === 'calcFormField') {
        const fieldKey = field.title.toLowerCase().replace(/\s+/g, '_')

        let fieldOptions: SelectOption[] = []

        // Handle options based on field type
        if (field.type === 'dropdown') {
          if (field.industryDataOptions) {
            // Handle industry data
            fieldOptions = Object.entries(industryData).map(([key, industry]) => ({
              value: key,
              label: industry.name,
            }))
          } else if (field.options) {
            // Handle other dropdowns like business size
            fieldOptions = field.options.split('\n').map((opt) => {
              const [key, ...labelParts] = opt.split(':')
              return {
                value: key.trim(),
                label: labelParts.join(':').trim(),
              }
            })
          }
        }

        acc[fieldKey as string] = {
          type: field.type,
          title: field.title,
          width: parseInt(field.width) || 12,
          info: field.info,
          category: field.category,
          prefix: field.prefix ?? '',
          suffix: '',
          order: field.order,
          options: fieldOptions,
        }
      }
      return acc
    }, {})
  }, [formFields])

  // Use processedFormFields instead of formFields in your component
  const [formData, setFormData] = useState(() => {
    if (initialFormData) {
      return initialFormData
    }
    return {}
  })

  // Transform industry data into sorted array for select
  const sortedIndustries = useMemo(() => {
    return Object.entries(industryData)
      .map(([key, industry]) => ({
        _uid: key,
        name: industry.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [])

  const handleInputChange = (name: string, value: string) => {
    setFormData((prev) => {
      const newState = {
        ...prev,
        [name]: value,
      }

      // If industry or business size changes, initialize slider values
      if (name === 'industrySelect' || name === 'sizeOfBusiness') {
        // Only proceed if both industry and size are selected
        if (newState.industrySelect && newState.sizeOfBusiness) {
          // Find all slider fields
          const sliderFields = formFields.filter(
            (field) => field.component === 'calcFormField' && field.type === 'slider'
          )

          // Set default values for each slider
          sliderFields.forEach((field) => {
            const fieldKey = field.key
            if (!fieldKey) return

            // Only set if the field doesn't already have a value
            if (prev[fieldKey as string] === undefined) {
              const range = getFieldRange(
                newState.industrySelect,
                newState.sizeOfBusiness,
                getIndustryDataKey(field) as keyof IndustryDataEntry
              )

              // Calculate the middle value
              const defaultValue = Math.floor((range.min + range.max) / 2)
              newState[fieldKey as string] = defaultValue.toString()
            }
          })
        }
      }

      return newState
    })
  }

  const [showResultsView, setShowResultsView] = useState(showResults)

  // Update the handleScoresCalculated function
  const handleScoresCalculated = (scores: {
    profitability: string
    capitalManagement: string
    growthReadiness: string
  }) => {
    setCalculatedScores(scores)
    setScoresReady(true)

    // If we're already showing results, submit the form with scores
    if (showResultsView && onFormSubmit && !formSubmitted) {
      onFormSubmit({
        ...formData,
        profitability_score: scores.profitability,
        capital_management_score: scores.capitalManagement,
        growth_readiness_score: scores.growthReadiness,
      })
    }
  }

  // Update the handleNext function
  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate phone before proceeding
    if (currentStep === totalSteps) {
      const phone = formData.phone || ''
      if (!isValidPhoneNumber(phone)) {
        setPhoneError(true)
        return // stop submission
      } else {
        setPhoneError(false)
      }
    }

    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1
      setCurrentStep(nextStep)
      setHighestStepReached(Math.max(highestStepReached, nextStep))
    } else {
      try {
        // Show results view first, which will calculate scores
        if (!hideResultsScreen) {
          setShowResultsView(true)
          // The form submission will happen after scores are calculated
          // via the handleScoresCalculated function
          return
        }

        // If we're hiding results, we need to submit without scores
        if (onFormSubmit) {
          onFormSubmit(formData)
        }
      } catch (error) {
        console.error('Error submitting form:', error)
      }
    }
  }

  const isValidPhoneNumber = (phone: string) => {
    if (!phone) return false

    // Reject if +<country code> is immediately followed by 0
    if (/^\+\d{1,3}0/.test(phone)) {
      return false
    }

    // Parse the number
    const phoneNumber = parsePhoneNumberFromString(phone)
    if (phoneNumber?.isValid()) return true

    // Fallback: try as local number with country detection
    let defaultCountry: string | undefined
    if (phone.startsWith('+61')) defaultCountry = 'AU'
    else if (phone.startsWith('+64')) defaultCountry = 'NZ'
    else defaultCountry = 'AU'

    const localNumber = parsePhoneNumberFromString(phone, {
      defaultCountry: defaultCountry as CountryCode,
    })
    return localNumber?.isValid() ?? false
  }

  useEffect(() => {
    if (scoresReady && showResultsView && !formSubmitted) {
      // Submit the form with the calculated scores
      const submitForm = async () => {
        console.warn('🚀 Starting form submission process...')

        if (!isValidPhoneNumber(formData.phone || '')) {
          setPhoneError(true)
          return
        }
        setPhoneError(false)
        setFormSubmitted(true)

        try {
          // Set formSubmitted to true to prevent duplicate submissions
          setFormSubmitted(true)

          const industryName = formData.industrySelect
            ? industryData[formData.industrySelect as string]?.name
            : undefined

          console.warn('📋 Company name from formData:', formData.company)

          const utmParams = new URLSearchParams(window.location.search)
          const utmSource = utmParams.get('utm_source')
          const utmMedium = utmParams.get('utm_medium')
          const utmContent = utmParams.get('utm_content')
          const utmTerm = utmParams.get('utm_term')

          // Read HubSpot tracking cookie for form submission attribution
          const hutk = document.cookie
            .split('; ')
            .find((c) => c.startsWith('hubspotutk='))
            ?.split('=')[1]

          // Create form data for HubSpot submission
          const formDataForSubmission = {
            contactProperties: {
              firstname: formData.first_name || '',
              lastname: formData.last_name || '',
              email: formData.email || '',
              phone: formData.phone || '',
              company: formData.company || '',
            },
            formProperties: {
              // Question responses - using HubSpot internal names
              to_what_extent_do_your_profits_consistently_convert_into_cashflow_:
                mapDropdownValueToLabel('profit_consistency', formData.profit_consistency ?? ''),
              how_effectively_do_you_track_and_manage_cash_flow_separately_from_profit_:
                mapDropdownValueToLabel('track_cashflow', formData.track_cashflow ?? ''),
              how_well_do_you_feel_the_money_invested_in_your_business_is_generating_strong_returns_:
                mapDropdownValueToLabel('q_money_invested', formData.q_money_invested ?? ''),
              how_dependent_is_your_business_on_outside_funding_to_maintain_operations_or_grow_:
                mapDropdownValueToLabel(
                  'q_dep_outside_funding',
                  formData.q_dep_outside_funding ?? ''
                ),
              how_well_is_your_business_financially_positioned_to_support_growth_and_expansion_:
                mapDropdownValueToLabel(
                  'q_futureGrowthAndExp',
                  formData.q_futureGrowthAndExp ?? ''
                ),

              // UTM parameters
              utm_source: utmSource ?? '',
              utm_medium: utmMedium ?? '',
              utm_content: utmContent ?? '',
              utm_term: utmTerm ?? '',

              // Calculated scores
              profitability_and_cash_flow_alignment_score: calculatedScores.profitability,
              capital_management_score: calculatedScores.capitalManagement,
              growth_readiness_score: calculatedScores.growthReadiness,
            },
            companyProperties: {
              name: formData.company || '',
              industry_class: industryName ?? '',
              hs_employee_range: formData.sizeOfBusiness || '',
              annualrevenue: formData.sales || '',
              annual_revenue_growth_rate: convertNumberToPercentage(formData.change_in_sales),
              net_operating_profit: formData.net_operating_profit || '',
              gross_margin: convertNumberToPercentage(formData.gross_margin),
              wage_expenses_to_annual_revenue: convertNumberToPercentage(
                formData.wage_exp_percentage
              ),
              net_operating_profit_margin: convertNumberToPercentage(
                formData.net_operating_profit_margin
              ),
            },
            sourceUrl: window.location.href,
            hutk: hutk || '',
          }

          // Log form data before submission
          console.warn(
            '📤 Form data for submission:',
            JSON.stringify(formDataForSubmission, null, 2)
          )

          // Push Enhanced Conversion data to dataLayer for Google Ads

          const phoneNumber = parsePhoneNumberFromString(formData.phone || '')
          const formattedPhone = phoneNumber?.format('E.164') || ''
          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            event: 'calculatorFormSubmit',
            email: formData.email || '',
            phone_number: formattedPhone,
            first_name: formData.first_name || '',
            last_name: formData.last_name || '',
            address: {
              country: phoneNumber?.country || 'AU',
            },
          })

          // Submit form to HubSpot CRM API (creates both Contact and Company)
          const response = await fetch('/api/hubspot/contact', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formDataForSubmission),
          })

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            console.error('HubSpot Form Submission Error:', errorData)
            throw new Error(`Failed to submit form: ${response.status}`)
          }

          // Form submitted successfully to HubSpot
          // Push success event to dataLayer
          window.dataLayer.push({
            event: 'calculatorFormSubmitSuccess',
          })

          // Form submitted successfully - PDF upload will be handled by Results component
        } catch (error) {
          console.error('Error submitting form to HubSpot:', error)
          // Keep formSubmitted as true to prevent infinite retry loop
          // The form data is still valid and shown to the user
        }
      }

      submitForm()
    }
  }, [scoresReady, calculatedScores, showResultsView, formSubmitted, formData])

  // Update URL when step changes
  useEffect(() => {
    if (!isClientSide) return

    const currentParams = new URLSearchParams(searchParams?.toString() || '')

    if (formSubmitted) {
      currentParams.set('step', 'results')
    } else if (currentStep === 1) {
      // Always remove step param for step 1
      currentParams.delete('step')
    } else if (currentStep >= highestStepReached) {
      // Only update step param if we're at the highest step reached and not on step 1
      currentParams.set('step', currentStep.toString())
      setHighestStepReached(currentStep)
    } else {
      // We're going backwards, remove the step param
      currentParams.delete('step')
    }

    // Update URL without reload
    window.history.replaceState(null, '', `${pathname}?${currentParams.toString()}`)
  }, [currentStep, formSubmitted, isClientSide, pathname, searchParams, highestStepReached])

  // Initialize step from URL on mount
  useEffect(() => {
    if (!isClientSide) return

    const stepFromUrl = searchParams?.get('step')
    if (stepFromUrl !== null && !isNaN(Number(stepFromUrl))) {
      setCurrentStep(Number(stepFromUrl))
    }
  }, [searchParams, isClientSide])

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Helper function to map field names to industry data keys
  const getIndustryDataKey = (field: CalcFormField) => {
    // Map the field title to the corresponding industry data key
    const fieldMapping: { [key: string]: keyof IndustryDataEntry } = {
      'Net operating profit': 'net_operating_profit',
      Sales: 'sales',
      'Gross margin': 'gross_margin',
      'Net operating profit margin': 'net_operating_profit_margin',
      'Wage expense to revenue': 'wage_exp_percentage',
      // Add other mappings as needed
    }
    return fieldMapping[field.title]
  }

  // Update getFieldRange to use the extracted size
  const getFieldRange = (
    industryId: string,
    businessSize: string,
    fieldKey: keyof IndustryDataEntry
  ) => {
    // Default range for different metrics
    const defaultRanges: { [key: string]: { min: number; max: number } } = {
      net_operating_profit: { min: 70500, max: 1000000 },
      sales: { min: 0, max: 15000000 }, // Increased to $15M
      gross_margin: { min: 0, max: 100 }, // Percentage
      net_operating_profit_margin: { min: 0, max: 100 }, // Percentage
      wage_exp_percentage: { min: 0, max: 100 }, // Percentage
    }

    if (
      industryId === null ||
      industryId === undefined ||
      businessSize === null ||
      businessSize === undefined ||
      industryData[industryId as string] === undefined
    ) {
      console.warn(`Using default range for: ${fieldKey}`)
      return defaultRanges[fieldKey as string] ?? { min: 0, max: 100 }
    }

    const industry = industryData[industryId as string]
    const sizeKey = businessSize.split(':')[0]
    const sizeData = industry.entries[sizeKey as keyof typeof industry.entries]

    if (sizeData === null || sizeData === undefined) {
      console.warn(`No size data found for key: ${sizeKey}, using default range`)
      return defaultRanges[fieldKey as string] ?? { min: 0, max: 100 }
    }

    // Get all values for this field
    const values = [
      parseFloat(String(sizeData['very-low']?.[fieldKey as string] ?? '0')),
      parseFloat(String(sizeData['low']?.[fieldKey as string] ?? '0')),
      parseFloat(String(sizeData['typical']?.[fieldKey as string] ?? '0')),
      parseFloat(String(sizeData['high']?.[fieldKey as string] ?? '0')),
      parseFloat(String(sizeData['very-high']?.[fieldKey as string] ?? '0')),
    ].filter((value) => !isNaN(value) && value >= 0)

    if (values.length === 0) {
      console.warn(`No valid values found for fieldKey: ${fieldKey}, using default range`)
      return defaultRanges[fieldKey as string] ?? { min: 0, max: 100 }
    }

    // Calculate range with some padding
    const min = Math.floor(Math.min(...values))
    const max = Math.ceil(Math.max(...values) * 1.2) // Add 20% to max for some headroom

    // Ensure the range doesn't exceed reasonable limits
    return {
      min: Math.max(0, min),
      max: Math.min(max, defaultRanges[fieldKey as string]?.max ?? 100),
    }
  }

  // Add handler to toggle between form and results
  const handleToggleView = () => {
    setShowResultsView(!showResultsView)
  }

  // Early return for results view - add hideResultsScreen check
  if (showResultsView && !hideResultsScreen) {
    return (
      <div className={styles.calculatorResultsContainer}>
        <Results
          isOpen={true}
          onClose={handleToggleView}
          formData={formData}
          processedFormFields={processedFormFields}
          industryData={industryData}
          ratings={ratings}
          printData={printData}
          onScoresCalculated={handleScoresCalculated}
          disclaimer={disclaimer}
          onDownload={onDownload}
          shouldUploadToStoryblok={formSubmitted}
        />
      </div>
    )
  }

  const handlePhoneChange = (value: string) => {
    // Remove everything except digits and '+'
    value = '+' + value.replace(/\D/g, '')

    // Detect country code prefix
    let defaultCountry: string | undefined
    if (value.startsWith('+61')) {
      defaultCountry = 'AU'
      // Remove any 0 immediately after +61
      value = '+61' + value.slice(3).replace(/^0+/, '')
    } else if (value.startsWith('+64')) {
      defaultCountry = 'NZ'
      value = '+64' + value.slice(3).replace(/^0+/, '')
    } else {
      defaultCountry = 'AU' // fallback
    }

    // Format number as user types
    const formatted = new AsYouType({ defaultCountry: defaultCountry as CountryCode }).input(value)

    // Update form state
    handleInputChange('phone', formatted)

    // Validate live and show red error message if invalid
    setPhoneError(!isValidPhoneNumber(formatted))
  }

  // Regular form view
  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <div className={styles.calculatorContainer}>
        <div className={styles.header}>
          <h2>Ready to see how your business stacks up?</h2>
          <p>
            Enter your details and key financial metrics to access your
            <br /> free business performance analysis.
          </p>
        </div>
        <h2 className={styles.title}>Industry Benchmarking Tool</h2>

        {/* Step indicator */}
        <div className={styles.stepIndicator}>
          {Object.keys(steps).map((step) => (
            <div
              key={step}
              className={`${styles.step} ${parseInt(step) === currentStep ? styles.active : ''}
                       ${parseInt(step) < currentStep ? styles.completed : ''}`}
            >
              Step {step}
            </div>
          ))}
        </div>

        <form onSubmit={handleNext} className={styles.form}>
          {/* Only show fields for current step */}
          {steps[currentStep as number]?.map((field: CalcFormField) => {
            if (field.component === 'calcFormHeading') {
              return (
                <h2 key={field._uid} className={styles.sectionHeading}>
                  {field.title}
                </h2>
              )
            }

            return (
              <div
                key={field._uid}
                className={
                  styles.formGroup + ' ' + (field.width === 'full' ? styles.fullWidth : '')
                }
              >
                <label
                  htmlFor={field.key}
                  className={styles.label + ' ' + (styles[field.key as keyof typeof styles] ?? '')}
                >
                  <div className={styles.labelContainer}>
                    <div className={styles.label}>{field.title}</div>
                    {field.info !== null && field.info !== undefined && field.info.length > 0 && (
                      <TooltipPrimitive.Provider>
                        <TooltipPrimitive.Root delayDuration={0}>
                          <TooltipPrimitive.Trigger asChild>
                            <div className={styles.info} aria-label="Additional information">
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clipPath="url(#clip0_144_122)">
                                  <path
                                    d="M7.5 0C3.35801 0 0 3.35801 0 7.5C0 11.642 3.35801 15 7.5 15C11.642 15 15 11.642 15 7.5C14.9956 3.35977 11.6402 0.00439453 7.5 0ZM7.5 13.7499C4.04824 13.7499 1.2501 10.9518 1.2501 7.5C1.2501 4.04824 4.04824 1.2501 7.5 1.2501C10.9518 1.2501 13.7499 4.04824 13.7499 7.5C13.7461 10.9503 10.95 13.7461 7.5 13.7499Z"
                                    fill="#0EACBD"
                                  />
                                  <path
                                    d="M7.4998 6.24993H6.8749C6.52979 6.24993 6.25 6.52971 6.25 6.87483C6.25 7.21995 6.52979 7.49973 6.8749 7.49973H7.4998V11.2497C7.4998 11.5948 7.77959 11.8746 8.12471 11.8746C8.46982 11.8746 8.74961 11.5948 8.74961 11.2497V7.49973C8.74961 6.8095 8.19004 6.24963 7.49951 6.24963L7.4998 6.24993Z"
                                    fill="#0EACBD"
                                  />
                                  <path
                                    d="M7.5 5.00012C8.01777 5.00012 8.4375 4.58039 8.4375 4.06262C8.4375 3.54486 8.01777 3.12512 7.5 3.12512C6.98223 3.12512 6.5625 3.54486 6.5625 4.06262C6.5625 4.58039 6.98223 5.00012 7.5 5.00012Z"
                                    fill="#0EACBD"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_144_122">
                                    <rect width="15" height="15" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </div>
                          </TooltipPrimitive.Trigger>
                          <TooltipPrimitive.Portal>
                            <TooltipPrimitive.Content
                              className={styles.infoPopover}
                              sideOffset={5}
                              side="top"
                              align="center"
                            >
                              <div className={styles.infoIcon}>
                                <InfoIcon />
                              </div>
                              <div className={styles.infoContent}>
                                {/* This is magic to make everything before the ':' bold */}
                                {field.info && field.info.includes(':') ? (
                                  <>
                                    <strong>{field.info.split(':', 1)[0]}</strong>
                                    {': ' + field.info.substring(field.info.indexOf(':') + 1)}
                                  </>
                                ) : (
                                  field.info
                                )}
                              </div>
                            </TooltipPrimitive.Content>
                          </TooltipPrimitive.Portal>
                        </TooltipPrimitive.Root>
                      </TooltipPrimitive.Provider>
                    )}
                  </div>
                </label>
                {field.type === 'dropdown' && (
                  <PhnxFormselect
                    variant="valueLabel"
                    name={field.key}
                    options={
                      field.industryDataOptions
                        ? sortedIndustries.map((industry) => ({
                            value: industry._uid,
                            label: industry.name,
                          }))
                        : field.options
                            .split('\n')
                            .map((opt) => {
                              const [value, ...labelParts] = opt.split(':')
                              const fullLabel = labelParts.join(':').trim()

                              // For business size, show only the numbers in the UI
                              // but keep the full value for CRM submission
                              if (field.key === 'sizeOfBusiness') {
                                // Extract just the number range from labels like "1-4 (Micro)"
                                const match = fullLabel.match(/^(\d+-\d+)/)
                                return {
                                  value: value.trim(),
                                  label: match ? match[1] : fullLabel,
                                }
                              }

                              return {
                                value: value.trim(),
                                label: fullLabel,
                              }
                            })
                            .sort((a, b) => {
                              if (field.key === 'sizeOfBusiness') {
                                return 0 // Keep original order
                              }
                              const numA = parseFloat(a.value)
                              const numB = parseFloat(b.value)
                              if (!isNaN(numA) && !isNaN(numB)) {
                                return numA - numB
                              }
                              return a.value.localeCompare(b.value)
                            })
                    }
                    isRequired
                    value={formData[field.key] || ''}
                    onChange={(value) => {
                      handleInputChange(field.key, value)
                    }}
                  />
                )}

                {field.type === 'slider' && (
                  <PhnxFormSlider
                    name={field.key}
                    isRequired
                    disabled={!formData.industrySelect || !formData.sizeOfBusiness}
                    min={
                      getFieldRange(
                        formData.industrySelect,
                        formData.sizeOfBusiness,
                        getIndustryDataKey(field) as keyof IndustryDataEntry
                      ).min
                    }
                    max={
                      getFieldRange(
                        formData.industrySelect,
                        formData.sizeOfBusiness,
                        getIndustryDataKey(field) as keyof IndustryDataEntry
                      ).max
                    }
                    value={parseFloat(formData[field.key] || '0')}
                    step={1}
                    prefix={field.prefix}
                    showMinAsOrLess={field.key === 'net_operating_profit' || field.key === 'sales'}
                    showMaxAsOrMore={field.key === 'net_operating_profit' || field.key === 'sales'}
                    onChange={(value) => {
                      handleInputChange(field.key, value.toString())
                    }}
                  />
                )}

                {field.type !== 'dropdown' && field.type !== 'slider' && (
                  <div
                    className={`
                      ${styles.inputContainer}
                      ${field.key === 'name' ? styles.nameContainer : ''}
                      ${field.key === 'company' ? styles.fullWidth : ''}
                      ${field.key === 'contact' ? styles.contactContainer : ''}
                    `}
                  >
                    {field.key === 'name' && (
                      <>
                        <PhnxForminput
                          variant="text"
                          name="first_name"
                          placeholder="First name"
                          isRequired
                          key={field.key + 'first_name'}
                          value={formData.first_name || ''}
                          onChange={(value) => handleInputChange('first_name', value)}
                        />
                        <PhnxForminput
                          variant="text"
                          name="last_name"
                          placeholder="Last name"
                          isRequired
                          key={field.key + 'last_name'}
                          value={formData.last_name || ''}
                          onChange={(value) => handleInputChange('last_name', value)}
                        />
                      </>
                    )}

                    {field.key === 'company' && (
                      <PhnxForminput
                        variant="text"
                        name={'company'}
                        placeholder="Company name"
                        isRequired
                        key={field.key + 'company'}
                        value={formData[field.key] || ''}
                        onChange={(value) => handleInputChange(field.key, value)}
                      />
                    )}

                    {field.key === 'contact' && (
                      <>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <PhnxForminput
                            variant="text"
                            name="phone"
                            placeholder="+61 123 456 789"
                            isRequired
                            key={field.key + 'phone'}
                            value={formData.phone || '+61'}
                            onChange={handlePhoneChange}
                          />
                          {phoneError && (
                            <div style={{ color: 'red', fontSize: '12px' }}>
                              Please enter a valid phone number
                            </div>
                          )}
                        </div>
                        <PhnxForminput
                          variant="email"
                          name="email"
                          placeholder="Email address"
                          isRequired
                          key={field.key + 'email'}
                          value={formData.email || ''}
                          onChange={(value) => handleInputChange('email', value)}
                        />
                      </>
                    )}

                    {/* if non of the above, render out the field with the prefix */}
                    {field.type !== 'dropdown' &&
                      field.type !== 'slider' &&
                      field.type !== 'text' && (
                        <div className={styles.inputContainer}>
                          <PhnxForminput
                            variant="number"
                            name={getFieldName(field)}
                            isRequired
                            value={formData[getFieldName(field)] || ''} // Ensure persisted value is used
                            onChange={(value) => {
                              handleInputChange(field.key, value)
                            }}
                          />
                          {field.prefix !== null &&
                            field.prefix !== undefined &&
                            field.prefix !== '' && (
                              <span className={styles.prefix}>{field.prefix}</span>
                            )}
                        </div>
                      )}
                  </div>
                )}
              </div>
            )
          })}

          <div className={styles.navigationButtons}>
            {currentStep > 1 && (
              <PhnxButton variant="tertiary" label="Back" onClick={handlePrevious} type="button" />
            )}
            <PhnxButton
              variant="primary"
              label={currentStep === totalSteps ? 'Get my results' : 'Submit and continue'}
              type="submit"
            />
          </div>

          <div className={styles.disclaimer}>
            {typeof disclaimer === 'object' &&
            disclaimer !== null &&
            ('content' in disclaimer || 'richtext' in disclaimer) ? (
              <div>{(disclaimer as Disclaimer).content ?? (disclaimer as Disclaimer).richtext}</div>
            ) : (
              (disclaimer as React.ReactNode)
            )}
          </div>
        </form>
      </div>
    </TooltipPrimitive.Provider>
  )
}

export default CalculatorForm
